import useTranslate from 'modules/local/useTranslate'
import React, {useMemo, useState} from 'react'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {announcement_create_Api, announcement_delete_Api, announcement_update_Api} from 'apis'
import {NullAsDefault} from 'views/Shared'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {createModalTitle} from 'components/renderFunctions/createModalTitle'
import {getId} from 'apis/model/base'
import {Modal} from 'antd'
import CRUD from 'views/Shared/enums/CRUD'
import {AnnouncementCreateForm, AnnouncementUpdateForm} from './AnnouncementForm'

// Generic theo owner_type (organization | group | course | course_class | ... về sau) - CHỈ 2 prop
// mô tả "đăng cho ai": `ownerItem` (object owner đã chọn, luôn 1-1 với owner_type - object organization/
// group/course/course_class TÙY owner_type) + `owner_id`/`{owner_type}_id` field gửi backend suy ra
// THẲNG từ tên owner_type (đã đúng convention sẵn có: organization_id/group_id/course_id/
// course_class_id) - KHÔNG cần fork theo owner_type ở tầng submit này nữa, thêm owner_type mới
// (vd tương lai) KHÔNG phải sửa file này. `groups` là NGOẠI LỆ DUY NHẤT (chỉ owner_type=organization
// cần - danh sách phòng ban con để chọn, không nằm trong object organization) nên vẫn giữ prop riêng.
export const useAnnouncementForm = ({
                                        owner_type,
                                        ownerItem,
                                        groups,
                                        defaultGroup,
                                        notifyListeners = {
                                            onCreated: undefined,
                                            onUpdated: undefined,
                                            onDeleted: undefined,
                                        },
                                    }) => {
    const t = useTranslate()
    const isOrganization = owner_type === 'organization'

    const [refresh_token, set_refresh_token] = useState()

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const {
        onCreated = NullAsDefault,
        onUpdated = NullAsDefault,
        onDeleted = NullAsDefault,
    } = notifyListeners ?? {}

    const onCancel = () => {
        setAction({})
    }

    const onRefresh = () => {
        onCancel()
        set_refresh_token(Date.now())
    }

    const onError = notifyOnError(t)

    const {handleAsyncAction: onCreate, ...creatingAsync} = useAsyncAction({
        onError,
        apiInfo: announcement_create_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.CREATE, t)
            onCreated(result, data)
            onRefresh()
        },
    })

    const {handleAsyncAction: onUpdate, ...updatingAsync} = useAsyncAction({
        onError,
        apiInfo: announcement_update_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onUpdated(result, data)
            onCancel()
        },
    })

    const {handleAsyncAction: onDelete} = useAsyncAction({
        onError,
        apiInfo: announcement_delete_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.DELETE, t)
            onDeleted(result, data)
            onRefresh()
        },
    })

    const handleAction = (action, params) => {
        switch (action) {
            case CRUD.CREATE:
            case CRUD.UPDATE:
                setAction({
                    type: action,
                    value: params,
                })
                break
            case CRUD.DELETE:
                showDeleteConfirmDialog({
                    translate: t,
                    title: 'delete this item',
                    message: 'are you sure you want to delete this',
                    onOk: () => {
                        onDelete({}, {id: params?.id})
                    },
                })
            default:
                break
        }
    }

    const modal = useMemo(() => {
        let params = {}
        let modal_params = {}

        switch (action?.type) {
            case CRUD.CREATE:
                modal_params = {
                    title: createModalTitle('create an announcement'),
                    width: 600,
                }
                params = {
                    initialValues: {
                        ...(action?.value || {}),
                        ownerItem,
                        // group_id: phòng ban con MẶC ĐỊNH chọn sẵn - CHỈ organization cần (course/
                        // group/course_class không có khái niệm phòng ban con).
                        ...(isOrganization && defaultGroup?.id ? {group_id: defaultGroup?.id} : {}),
                    },
                    isSubmitting: creatingAsync.isLoading,
                    onSubmit: (values) => {
                        // {owner_type}_id: suy generic từ owner_type - ĐÚNG convention field name backend
                        // đang dùng cho MỌI owner_type (organization_id/group_id/course_id/course_class_id).
                        onCreate({...values, owner_type, [`${owner_type}_id`]: getId(ownerItem)}, {})
                    },
                }
                break
            case CRUD.UPDATE:
                const announcement_id = getId(action?.value)
                modal_params = {
                    title: createModalTitle('edit announcement'),
                    width: 600,
                }
                params = {
                    initialValues: {...action?.value},
                    isSubmitting: updatingAsync.isLoading,
                    onSubmit: (values) => {
                        onUpdate(values, {id: announcement_id})
                    },
                }
                break
            default:
                return null
        }

        const Component = action?.type === CRUD.UPDATE ? AnnouncementUpdateForm : AnnouncementCreateForm

        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                open={action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <Component
                    {...{onCancel}}
                    {...params}
                    owner_type={owner_type}
                    ownerItem={ownerItem}
                    groups={groups}
                    defaultGroup={defaultGroup}
                />
            </Modal>
        )
    }, [action, owner_type, ownerItem, groups, defaultGroup, creatingAsync.isLoading, updatingAsync.isLoading])

    return {
        modal,
        action,
        handleAction,
        refresh_token,
        onRefresh,
    }
}
